/*
 * Copyright (c) 2023-2025 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import React, { forwardRef } from "react";
import { Card } from "reactstrap"
import LoginTypePieChart from "./components/pieCharts/LoginTypePieChart";
import LoyaltyPieChart from "./components/pieCharts/LoyaltyPieChart";
import GDPRPieChart from "./components/pieCharts/GDPRPieChart";
import VisitsPieChart from "./components/pieCharts/VisitsPieChart";
import AgeBarChart from "./components/barCharts/AgeBarChart";
import DurationBarChart from "./components/barCharts/DurationBarChart";
import LoyaltyBarChart from "./components/barCharts/LoyaltyBarChart";
import AutomationsBarChart from "./components/barCharts/AutomationsBarChart";
import LoginMetrics from "./components/metrics/LoginMetrics";
import GuestMetrics from "./components/metrics/GuestMetrics";
import MarketingMetrics from "./components/metrics/MarketingMetrics";
import LiveActivityFeed from "./components/other/LiveActivityFeed";

// constants
export const gridLayoutConstants = {
  rowHeight: 60,
  cols: 12,
  minCols: 3,
  minRows: 2,
  maxRows: 10
};

// helper functions
export const layoutEncodingToTemplate = (layoutEncoding) => {
  return layoutEncoding.map((item, index) => ({
    i: index.toString(),
    x: item.row,
    y: item.col,
    h: item.height,
    w: item.width,
    minW: gridLayoutConstants.minCols,
    minH: gridLayoutConstants.minRows,
    maxH: gridLayoutConstants.maxRows,
  }));
};

export const layoutEncodingToContent = (layoutEncoding) => {
  return layoutEncoding.map((item, index) => ({
    i: index.toString(),
    type: item.type
  }));
};

export const decodeLayout = (layoutEncoding) => {
  if (layoutEncoding == null) {
    return null;
  }
  return {
    template: layoutEncodingToTemplate(layoutEncoding),
    content: layoutEncodingToContent(layoutEncoding)
  };
};

export const encodeLayout = (layout) => {
  return layout.template.map((item) => ({
    row: item.x,
    col: item.y,
    height: item.h,
    width: item.w,
    type: layout.content.find((content) => content.i === item.i)?.type ?? ""
  }));
};

// mappings
export const gridItemAttributes = {
  loginTypePie: {
    element: LoginTypePieChart,
    name: "Login Type Pie Chart",
    type: "pie",
  },
  // genderPie: {
  //   element: GenderPieChart,
  //   name: "Gender Pie Chart",
  //   type: "pie",
  // },
  loyaltyPie: {
    element: LoyaltyPieChart,
    name: "Loyalty Pie Chart",
    type: "pie",
  },
  gdprPie: {
    element: GDPRPieChart,
    name: "GDPR Compliance Pie Chart",
    type: "pie",
  },
  visitsPie: {
    element: VisitsPieChart,
    name: "Visit Count Pie Chart",
    type: "pie",
  },
  ageBar: {
    element: AgeBarChart,
    name: "Age Range Bar Chart",
    type: "bar",
  },
  durationBar: {
    element: DurationBarChart,
    name: "Session Duration Bar Chart",
    type: "bar",
  },
  loyaltyBar: {
    element: LoyaltyBarChart,
    name: "Loyalty Bar Chart",
    type: "bar",
  },
  automationsBar: {
    element: AutomationsBarChart,
    name: "Automations Bar Chart",
    type: "bar",
  },
  loginMetrics: {
    element: LoginMetrics,
    name: "Login Metrics",
    type: "metrics",
  },
  guestSessionMetrics: {
    element: GuestMetrics,
    name: "Guest Metrics",
    type: "metrics",
  },
  marketingMetrics: {
    element: MarketingMetrics,
    name: "Marketing Metrics",
    type: "metrics",
  },
  liveActivityFeed: {
    element: LiveActivityFeed,
    name: "Live Activity Feed",
    type: "other"
  }
};

// default encodings
export const defaultLayoutEncoding = [
  {
    col: 0,
    height: 3,
    row: 0,
    type: "loyaltyBar",
    width: 9
  },
  {
    col: 0,
    height: 12,
    row: 9,
    type: "liveActivityFeed",
    width: 3
  },
  {
    col: 3,
    height: 3,
    row: 0,
    type: "visitsPie",
    width: 5
  },
  {
    col: 6,
    height: 3,
    row: 0,
    type: "loginTypePie",
    width: 5
  },
  {
    col: 3,
    height: 3,
    row: 5,
    type: "guestSessionMetrics",
    width: 4
  },
  {
    col: 6,
    height: 3,
    row: 5,
    type: "marketingMetrics",
    width: 4
  },
  {
    col: 9,
    height: 3,
    row: 0,
    type: "automationsBar",
    width: 9
  }
];

export const defaultLayouts = [{
  name: "Default",
  encoding: defaultLayoutEncoding
}];

// elements
export const GridCard = forwardRef((props, ref) => {
  return <Card {...props} innerRef={ref} />;
});
